import {getDiffArray} from '%helpers%/utils';
import {initTomMultiCheckboxSelect, initTomSelect} from '%helpers%/helpers';

class MultipleSelectFilter {
  constructor(el, config) {
    if (!el || !config || !config.controls.length) return;

    this.dropdown = el;
    this.toggle = this.dropdown.querySelector('.dropdown-toggle');
    this.controls = this.dropdown.querySelectorAll('select.select-control');

    this.withDropdown = !!this.toggle;
    this.bsDropdown = this.withDropdown ? bootstrap.Dropdown.getOrCreateInstance(this.toggle) : null;

    this.multiple = this.controls.length > 1;
    this.tabs =
      this.multiple && this.dropdown.querySelectorAll('[data-bs-toggle="pill"], [data-bs-toggle="tab"]');
    this.tabActive = this.multiple && this.tabs[0];

    this.config = {
      controls: [],
      hasTags: false,
      tagsListId: null,
      ...config,
    };

    this.tagsList = config.tagsListId && document.getElementById(this.config.tagsListId);
    this.tagsNavbar = this.tagsList && this.tagsList.closest('.tags-navbar');
    this.btnClear = config.btnClearId && document.getElementById(this.config.btnClearId);
    this.parentOffcanvas = this.dropdown.closest('.widget-offcanvas');

    this.hasTags = this.config.hasTags && this.tagsList;

    this.init();
  }

  init() {
    this.initControls();
    this.setListeners();
  }

  initControls() {
    const initControl = ({id, settings}) => {
      const control = document.getElementById(id);
      const tomSettings = _.merge(
        {
          hasTags: this.config.hasTags,
          controlClass: 'ts-control form-control',
          searchEnabled: true,
          closeAfterSelect: true,
          hideSelected: true,
        },
        this.withDropdown
          ? {
              type: control.multiple ? 'selectCheckboxes' : 'select',
              preload: true,
              hideSelected: false,
              dropdownParent: control.closest('.control-wrapper'),
              closeAfterSelect: false,
              plugins: {
                dropdown_input: this.withDropdown,
                dropdown_header: false,
              },
            }
          : {},
        settings
      );

      if (!tomSettings.searchEnabled) tomSettings.plugins['dropdown_input'] = false;

      if (tomSettings.type === 'selectCheckboxes') initTomMultiCheckboxSelect(control, tomSettings);
      else initTomSelect(control, tomSettings);

      if (tomSettings.openOnFocus) control.tomselect.open();
    };

    this.config.controls.forEach(initControl);

    if (this.hasTags) {
      this.renderTags();
      this.checkEmptyTags();
    }
  }

  getCount() {
    let count = 0;
    this.controls.forEach(control => {
      if (!control.disabled) count += control.tomselect.items.length;
    });
    return count;
  }

  setCount(count) {
    const badge = this.dropdown.querySelector('.ts-count');
    if (!badge) return;
    badge.innerText = count;
    badge.classList[count > 0 ? 'add' : 'remove']('visible');
  }

  cancel() {
    this.cancelTempValue();
    if (this.withDropdown) this.bsDropdown.hide();
  }

  apply() {
    this.setTempValue();
    if (this.withDropdown) this.bsDropdown.hide();
  }

  toggleDropdown(isShown) {
    if (!this.withDropdown) return;
    this.bsDropdown[isShown ? 'show' : 'hide']();
    if (!isShown) this.dropdown.querySelector('.dropdown-active')?.classList.remove('dropdown-active');
  }

  setTempValue() {
    this.controls.forEach(control => {
      control.tomselect.tempValue = control.multiple
        ? [...control.tomselect.getValue()]
        : control.tomselect.getValue();
    });
  }

  cancelTempValue() {
    this.controls.forEach(control => {
      const prev = control.tomselect.tempValue || (control.multiple ? [] : '');
      if (
        control.multiple
          ? getDiffArray(control.tomselect.getValue(), prev).length
          : prev !== control.tomselect.getValue()
      ) {
        control.tomselect.setValue(prev);
      }
    });
  }

  toggleChecked(control, target) {
    const tom = control.tomselect;
    if (target.classList.contains('checked') || target.classList.contains('all-checked')) {
      tom.setValue([]);
    } else {
      const allValues = Object.keys(tom.options).map(key => tom.options[key][tom.settings.valueField]);
      tom.setValue(allValues);
    }
    tom.refreshOptions();
    this._dispatchChangeEvent(control);
  }

  createTag(control, data) {
    const tom = control.tomselect;
    const value = data[tom.settings.valueField];
    const name = data[tom.settings.labelField];

    const tag = document.createElement('div');
    tag.className = 'btn-tag btn-outline-secondary btn btn-sm btn-tag-dismissible';
    tag.dataset.value = value;
    tag.dataset.target = `#${control.id}`;
    tag.innerHTML = `
      <span class="tag-inner">${name}</span>
      <span role="button" class="btn-tag-remove btn btn-link btn-link-inline p-0" data-ts-action="deleteTag">
        <svg class="svg-icon icon-close svg-icon-sm" aria-hidden="true" focusable="false"><use xlink:href="/images/spritemap/spritemap.svg#icon-close"/></svg>
      </span>
    `;

    tag.querySelector('[data-ts-action="deleteTag"]').addEventListener('click', () => {
      tom.removeItem(value);
      this.setCount(this.getCount());
      tag.classList.add('fadeOut');
      setTimeout(() => tag.remove(), 300);
    });

    return tag;
  }

  renderTags() {
    const render = control => {
      const tom = control.tomselect;
      const currentTags = this.tagsList.querySelectorAll(`.btn-tag[data-target="#${control.id}"]`);

      tom.items.forEach(value => {
        if (![...currentTags].map(tag => tag.dataset.value).includes(value)) {
          const tag = this.createTag(control, tom.options[value]);
          this.tagsList.appendChild(tag);
        }
      });

      currentTags.forEach(tag => {
        if (!tom.items.includes(tag.dataset.value)) tag.remove();
      });
    };

    this.controls.forEach(control => {
      if (!control.tomselect.items.length) {
        this.tagsList.querySelectorAll(`.btn-tag[data-target="#${control.id}"]`).forEach(tag => tag.remove());
      } else if (!control.disabled) {
        render(control);
      }
    });
  }

  checkEmptyTags() {
    const isEmpty = this.tagsList.querySelectorAll('.btn-tag').length > 0;
    this.tagsNavbar?.classList[isEmpty ? 'remove' : 'add']('d-none');
    this.tagsList.classList[isEmpty ? 'remove' : 'add']('d-none');
    this.btnClear?.classList[isEmpty ? 'remove' : 'add']('d-none');
  }

  checkBtnToggleChecked(control) {
    const btn = control.closest('.control-wrapper')?.querySelector('[data-ts-action="toggleChecked"]');
    if (!btn) return;

    const selected = control.tomselect.getValue().length;
    const total = Object.keys(control.tomselect.options).length;

    btn.classList[selected > 0 && selected === total ? 'add' : 'remove']('all-checked', 'active');
    btn.classList[selected > 0 && selected !== total ? 'add' : 'remove']('checked');
  }

  checkTabDisabled() {
    if (!this.multiple) return;

    this.tabs.forEach(tab => {
      const control = this._getTabControl(tab);
      const disabled = this._isTabDisabled(tab);
      tab.disabled = disabled;
      control.disabled = disabled;
      if (disabled && control.tomselect.getValue().length) {
        control.tomselect.setValue([]);
      }
    });
  }

  _getTabControl(tab) {
    const {bsTarget} = tab.dataset;
    return this.dropdown.querySelector(`${bsTarget} select.select-control`);
  }

  _isTabDisabled(tab) {
    const control = this._getTabControl(tab);
    const {dependTarget = null} = control.dataset;
    if (!dependTarget) return false;
    const dependency = this.dropdown.querySelector(dependTarget)?.tomselect?.getValue() || [];
    return !dependency.length;
  }

  _handleChange(event) {
    const control = event.target;
    const count = control.tomselect.getValue().length;

    if (this.multiple) {
      const badge = this.dropdown.querySelector(`.ts-count[data-ts-target="#${control.id}"]`);
      if (badge) {
        badge.innerText = count;
        badge.classList[count > 0 ? 'add' : 'remove']('visible');
      }
    }

    this.setCount(this.getCount());
    this.checkBtnToggleChecked(control);
    if (this.multiple) this.checkTabDisabled();
    if (this.hasTags) {
      this.renderTags();
      this.checkEmptyTags();
    }

    this._dispatchChangeEvent(event);
  }

  _dispatchChangeEvent(event) {
    const changeEvent = new CustomEvent('change.filter', {
      detail: {event},
      bubbles: true,
      cancelable: true,
    });
    this.dropdown.dispatchEvent(changeEvent);
    if (this.hasTags) {
      this.tagsList.dispatchEvent(changeEvent);
      this.tagsList.querySelector('.collapse')?.dispatchEvent(new Event('update.cropped'));
    }
  }

  _handleOpen(e) {
    if (!this.withDropdown) return;

    const isOpen = e.type.includes('show');
    if (e.type.includes('dropdown')) {
      this.dropdown.querySelector('.dropdown-menu')?.classList[isOpen ? 'add' : 'remove']('active');
      if (isOpen) {
        this.controls.forEach(control => control.tomselect.open());
        if (this.multiple) this.checkTabDisabled();
      } else {
        if (this.multiple && !this.tabs[0].classList.contains('active')) this.tabs[0].click();
      }
    } else if (e.type.includes('tab')) {
      this.tabActive = e.target;
      const {bsTarget} = e.target.dataset;
      this.dropdown
        .querySelector(`${bsTarget} select.select-control`)
        ?.tomselect[isOpen ? 'open' : 'close']();
    }
  }

  _handleClickOutside(e) {
    console.log(this.dropdown.contains(e.target));
    if (this.withDropdown && this.bsDropdown && !this.dropdown.contains(e.target)) {
      this.bsDropdown.hide();
    }
  }

  setListeners() {
    if (this.withDropdown) {
      this.toggle.addEventListener('show.bs.dropdown', e => this._handleOpen(e));
      this.toggle.addEventListener('hidden.bs.dropdown', e => this._handleOpen(e));
      this.dropdown.addEventListener('shown.bs.tab', e => this._handleOpen(e));
      this.dropdown.addEventListener('hidden.bs.tab', e => this._handleOpen(e));

      if (this.controls.length && !this.controls[0].multiple) {
        this.controls[0].addEventListener('change', e => this.toggleDropdown(false));
      }

      this.dropdown.querySelectorAll('[data-bs-action], [data-bs-dismiss="dropdown"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const {bsAction, bsDismiss} = btn.dataset;
          if (bsDismiss === 'dropdown') this.cancel();
          else if (bsAction && typeof this[bsAction] === 'function') this[bsAction]();
        });
      });

      document.addEventListener('pointerdown', e => this._handleClickOutside(e), {
        passive: true,
      });
    }

    this.controls.forEach(control => {
      control.addEventListener('change', e => this._handleChange(e));

      control
        .closest('.control-wrapper')
        ?.querySelectorAll('[data-ts-action]')
        .forEach(btn => {
          btn.addEventListener('click', () => {
            const {tsAction} = btn.dataset;
            if (typeof this[tsAction] === 'function') this[tsAction](control, btn);
          });
        });
    });

    this.btnClear?.addEventListener('click', () => {
      this.controls.forEach(control => {
        if (control.tomselect) control.tomselect.setValue('');
        else control.value = '';
      });
    });

    if (this.parentOffcanvas) {
      this.parentOffcanvas.addEventListener('show.bs.offcanvas', () => {
        this.setTempValue();
      });
      this.parentOffcanvas.addEventListener(
        'shown.bs.offcanvas',
        () => {
          this.parentOffcanvas.querySelectorAll('[data-bs-dismiss="offcanvas"]').forEach(btn => {
            btn.addEventListener('click', () => {
              this.cancelTempValue();
            });
          });
          this.parentOffcanvas.querySelectorAll('[data-bs-apply="offcanvas"]').forEach(btn => {
            btn.addEventListener('click', () => {
              this.setTempValue();
            });
          });
        },
        {once: true}
      );
    }
  }
}

export default MultipleSelectFilter;
